import type {FilterDescriptor} from '@progress/kendo-data-query'
import {FilterOperator} from '@progress/kendo-data-query'
import type {SystemInterface} from '@/types/systems'
import type {WorldInterface} from '@/types/worlds'
import type {SwadeSkillInterface} from '@/types/items'
import type {AttributeNameType} from '@/types/characters'
import {useUxData} from "@/stores/ux-data.ts";

export interface PredicateContext {
    system?: SystemInterface | null
    worlds?: WorldInterface[]
    sources?: WorldInterface[]
    trashed?: boolean | null
}

const pushSystem = (out: FilterDescriptor[], ctx: PredicateContext) => {
    if (ctx.system) {
        out.push({ field: 'system.uuid', operator: FilterOperator.EqualTo, value: ctx.system.uuid })
    }
}

const pushWorlds = (out: FilterDescriptor[], ctx: PredicateContext) => {
    const worlds = (ctx.worlds ?? []).filter(Boolean)
    if (worlds.length > 0) {
        out.push({
            field: 'world.uuid',
            operator: FilterOperator.EqualTo,
            value: worlds.map(w => w.uuid),
        })
    }
}

const pushSource = (out: FilterDescriptor[], ctx: PredicateContext) => {
    const sources = (ctx.sources ?? []).filter(Boolean)
    if (sources.length > 0) {
        out.push({ field: 'source.uuid', operator: FilterOperator.EqualTo, value: sources.map(s => s.uuid) })
    }
}

const pushTrashed = (out: FilterDescriptor[], ctx: PredicateContext) => {
    if (ctx.trashed) {
        out.push({ field: 'element.dateDeleted', operator: FilterOperator.IsNotNull, value: ctx.trashed })
        out.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: ':user' })
    }
}

const pushStringPartial = (out: FilterDescriptor[], stringPartial?: string) => {
    if (stringPartial && stringPartial.length > 0) {
        out.push({ field: 'element.name', operator: FilterOperator.Contains, value: stringPartial })
    }
}

export interface SkillFilterInput {
    stringPartial?: string
    skills?: SwadeSkillInterface[]
    linkedAttributes?: AttributeNameType[]
}

export function buildSkillPredicates(ctx: PredicateContext, filters: SkillFilterInput): FilterDescriptor[] {
    const out: FilterDescriptor[] = []
    pushSystem(out, ctx)
    pushWorlds(out, ctx)
    pushSource(out, ctx)
    pushStringPartial(out, filters.stringPartial)
    if (filters.skills && filters.skills.length > 0) {
        out.push({
            field: 'skill.uuid',
            operator: FilterOperator.EqualTo,
            value: filters.skills.map(s => s.uuid),
        })
    }
    if (filters.linkedAttributes && filters.linkedAttributes.length > 0) {
        out.push({
            field: 'skill.linkedAttribute',
            operator: FilterOperator.EqualTo,
            value: filters.linkedAttributes,
        })
    }
    pushTrashed(out, ctx)
    return out
}

export interface EdgeFilterInput {
    stringPartial?: string
    types?: string[]
}

export function buildEdgePredicates(ctx: PredicateContext, filters: EdgeFilterInput): FilterDescriptor[] {
    const out: FilterDescriptor[] = []
    pushSystem(out, ctx)
    pushWorlds(out, ctx)
    pushSource(out, ctx)
    pushStringPartial(out, filters.stringPartial)
    if (Array.isArray(filters.types) && filters.types.includes('custom')) {
        const excludedTypes = useUxData().swadeAdvantageTypes.filter((el: { label: string, value: string }) => {
            return !(filters.types ?? []).includes(el.value) ;
        })
            .map((el) => {
                return el.value;
            })
        ;

        out.push({ field: 'edge.type', operator: FilterOperator.NotEqualTo, value: excludedTypes })
    }
    else if (Array.isArray(filters.types) && filters.types.length > 0) {
        out.push({ field: 'edge.type', operator: FilterOperator.EqualTo, value: filters.types })
    }
    pushTrashed(out, ctx)
    return out
}

export interface HindranceFilterInput {
    stringPartial?: string
    severity?: string[]
}

export function buildHindrancePredicates(ctx: PredicateContext, filters: HindranceFilterInput): FilterDescriptor[] {
    const out: FilterDescriptor[] = []
    pushSystem(out, ctx)
    pushWorlds(out, ctx)
    pushSource(out, ctx)
    pushStringPartial(out, filters.stringPartial)
    if (filters.severity && filters.severity.length > 0) {
        out.push({ field: 'hindrance.type', operator: FilterOperator.EqualTo, value: filters.severity })
    }
    pushTrashed(out, ctx)
    return out
}

export interface PowerFilterInput {
    stringPartial?: string
    ranks?: string[]
}

export function buildPowerPredicates(ctx: PredicateContext, filters: PowerFilterInput): FilterDescriptor[] {
    const out: FilterDescriptor[] = []
    pushSystem(out, ctx)
    pushWorlds(out, ctx)
    pushSource(out, ctx)
    pushStringPartial(out, filters.stringPartial)
    if (filters.ranks && filters.ranks.length > 0) {
        out.push({ field: 'power.details', operator: FilterOperator.Contains, value: filters.ranks })
    }
    pushTrashed(out, ctx)
    return out
}

// Setting rules and worlds take their keyword as an argument rather than from a filter object:
// each has two kinds of caller, and they must agree on everything except the keyword. The list
// surface (the rule grid, the world list, a world browser) passes the keyword its own search box
// wrote; a type-ahead passes the text typed into it, which never reaches the shared store.

export function buildSettingRulePredicates(
    ctx: Pick<PredicateContext, 'sources' | 'trashed'>,
    stringPartial: string,
): FilterDescriptor[] {
    const out: FilterDescriptor[] = []
    if (stringPartial.length > 0) {
        out.push({ field: 'settingRule.name', operator: FilterOperator.Contains, value: stringPartial })
    }
    // Setting rules are scoped by the selected source worlds (source.uuid IN [...]).
    pushSource(out, ctx)
    if (ctx.trashed) {
        out.push({ field: 'settingRule.dateDeleted', operator: FilterOperator.IsNotNull, value: ctx.trashed })
        out.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: ':user' })
    }
    return out
}

export interface WorldPredicateContext {
    system?: SystemInterface | null
    trashed?: boolean | null
    ownedOnly?: boolean
    // The library only: unowned worlds. The API reads `owner.*` criteria as "my worlds" (owned and
    // played-in), so this asks about the world's own owner column instead.
    unownedOnly?: boolean
    // Hidden: set from the user's access, never from a control. A user without premium access is
    // offered only worlds they can use; the API reads `isPremium eq false` as not premium (NULL or
    // false).
    excludePremium?: boolean
}

export function buildWorldPredicates(ctx: WorldPredicateContext, stringPartial: string): FilterDescriptor[] {
    const out: FilterDescriptor[] = []
    pushSystem(out, ctx)
    if (stringPartial.length > 0) {
        out.push({ field: 'world.name', operator: FilterOperator.Contains, value: stringPartial })
    }
    if (ctx.trashed) {
        out.push({ field: 'world.dateDeleted', operator: FilterOperator.IsNotNull, value: ctx.trashed })
        out.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: ':user' })
    }
    if (ctx.ownedOnly) {
        out.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: ':user' })
    }
    if (ctx.unownedOnly) {
        out.push({ field: 'world.owner', operator: FilterOperator.IsNull, value: true })
    }
    if (ctx.excludePremium) {
        out.push({ field: 'world.isPremium', operator: FilterOperator.EqualTo, value: false })
    }
    return out
}

export interface FrameworkFilterInput {
    stringPartial?: string
}

export function buildFrameworkPredicates(ctx: PredicateContext, filters: FrameworkFilterInput): FilterDescriptor[] {
    const out: FilterDescriptor[] = []
    pushSystem(out, ctx)
    pushWorlds(out, ctx)
    pushSource(out, ctx)
    pushStringPartial(out, filters.stringPartial)
    pushTrashed(out, ctx)
    return out
}
